import sortBy from "lodash/sortBy";

import { TASK_TITLE_MAX_LENGTH } from "@fartherfinance/frontend/api/Tasks/constants/common";
import type { GetTaskCommentsQueryParams } from "@fartherfinance/frontend/api/Tasks/requests/getTaskComments";
import type {
  GetTasksQueryParamsV2,
  TasksFilterQueryParamsV2,
} from "@fartherfinance/frontend/api/Tasks/requests/getTasks.v2";
import type { AssigneePayload } from "@fartherfinance/frontend/api/Tasks/requests/types";
import {
  TaskPriority,
  TaskSource,
  TaskStatus,
  TaskTypeV2,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  mapTaskPriorityToLabel,
  mapTaskTypeToLabelV2,
} from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import { Custodian, SortOrder } from "@fartherfinance/frontend/api/Types";

import type { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";

export const TASKS_DEFAULT_PAGE = 1;
const TASKS_DEFAULT_PAGE_SIZE = 20;

export const TASK_STATUSES_ACTIVE = [
  TaskStatus.enum.NOT_STARTED,
  TaskStatus.enum.IN_PROGRESS,
  TaskStatus.enum.WAITING_ON_CLIENT,
  TaskStatus.enum.WAITING_ON_THIRD_PARTY,
  TaskStatus.enum.WAITING_ON_ADVISOR,
  TaskStatus.enum.WAITING_ON_CUSTODIAN,
];

export const TASK_STATUSES_COMPLETED = [TaskStatus.enum.COMPLETED];

export const TASK_STATUSES_ALL = [
  ...TASK_STATUSES_ACTIVE,
  ...TASK_STATUSES_COMPLETED,
  TaskStatus.enum.CANCELED,
];

export const TASKS_DEFAULT_FILTERING_QUERY_PARAMS: TasksFilterQueryParamsV2 = {
  filterByTaskStatus: TASK_STATUSES_ACTIVE,
};

export const TASKS_DEFAULT_QUERY_PARAMS: GetTasksQueryParamsV2 = {
  page: TASKS_DEFAULT_PAGE,
  pageSize: TASKS_DEFAULT_PAGE_SIZE,
  sort: `${SortOrder.Ascending}dueDate`,
  ...TASKS_DEFAULT_FILTERING_QUERY_PARAMS,
};

export const TASK_COMMENTS_STATIC_QUERY_PARAMS: GetTaskCommentsQueryParams = {
  page: 1,
  pageSize: 999,
  sort: `${SortOrder.Ascending}createdTs`,
};

export const MIN_MAX_DUE_DATE_RANGE_YEARS = 2;
export const ATTACHMENTS_LIMIT_COUNT = 5;
export const TASK_TITLE_MAX_LENGTH_ERROR_MSG = `Task title cannot exceed ${TASK_TITLE_MAX_LENGTH} characters`;

export const custodianOptions = Custodian.options.map((custodian) => ({
  label: custodian,
  value: custodian,
}));

export const mapTaskSourceToLabel = (source: TaskSource): string => {
  switch (source) {
    case "MANUAL":
      return "Manually created";
    case "SYSTEM_GENERATED":
      return "System generated";
    case "EMAIL_TO_TASK":
      return "Email to task";
    default:
      return source;
  }
};

export const generationOptions = TaskSource.options.map((source) => ({
  label: mapTaskSourceToLabel(source),
  value: source,
}));

export const taskPriorityOptions = TaskPriority.options.map((taskPriority) => ({
  label: mapTaskPriorityToLabel(taskPriority),
  value: taskPriority,
}));

export const taskTypeOptionsV2 = sortBy(
  TaskTypeV2.options.map((taskType) => ({
    label: mapTaskTypeToLabelV2(taskType),
    value: taskType,
  })),
  "label"
);

export type TaskAssigneeAutocompleteOption<
  T extends string | undefined = string
> = EnhancedAutocompleteOption<T> & {
  meta: Pick<AssigneePayload, "type">;
};

export const assigneeDefaultOption: TaskAssigneeAutocompleteOption<undefined> =
  {
    label: "Service Team",
    value: undefined,
    meta: { type: "GROUP" },
  };
